(() => {
  if (window.MettaEditorial) return;
  const instances = new WeakMap();
  const motion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  function mountGoals(root, signal) {
    const panels = [...root.querySelectorAll('[data-v2-goal]')];
    const tablist = root.querySelector('[data-v2-goal-tabs]');
    if (!panels.length || !tablist) return;
    tablist.replaceChildren();
    let current = 0;
    const tabs = panels.map((panel, index) => {
      const tab = document.createElement('button');
      tab.type = 'button';
      tab.id = panel.id + '-tab';
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panel.id);
      tab.textContent = panel.dataset.label;
      tab.addEventListener('click', () => select(index), { signal });
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = current + 1;
        if (event.key === 'ArrowLeft') next = current - 1;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = panels.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        select(next, true);
      }, { signal });
      panel.setAttribute('role', 'tabpanel');
      // Store-wide tracking rewrites button IDs after mount, so give the
      // panel its own stable accessible name instead of referring to a tab ID.
      panel.setAttribute('aria-label', panel.dataset.label);
      const count = panel.querySelector('[data-v2-goal-count]');
      count.textContent = (index + 1) + ' / ' + panels.length;
      count.setAttribute('aria-label', root.dataset.positionLabel.replace('[current]', index + 1).replace('[total]', panels.length));
      panel.querySelector('[data-v2-goal-controls]').hidden = panels.length < 2;
      const media = panel.querySelector('[data-v2-goal-swipe]');
      media.querySelectorAll('img').forEach(image => { image.draggable = false; });
      let start;
      media.addEventListener('pointerdown', event => {
        if (event.target.closest('button, a') || event.button > 0) return;
        start = { x: event.clientX, y: event.clientY, id: event.pointerId };
        if (event.pointerType === 'mouse') event.preventDefault();
        media.setPointerCapture(event.pointerId);
      }, { signal });
      media.addEventListener('pointerup', event => {
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        start = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.4) select(current + (dx < 0 ? 1 : -1));
      }, { signal });
      media.addEventListener('pointercancel', () => { start = null; }, { signal });
      tablist.append(tab);
      return tab;
    });
    function select(index, focus = false) {
      current = (index + panels.length) % panels.length;
      panels.forEach((panel, i) => {
        panel.hidden = i !== current;
        tabs[i].setAttribute('aria-selected', String(i === current));
        tabs[i].tabIndex = i === current ? 0 : -1;
      });
      if (focus) tabs[current].focus({ preventScroll: true });
    }
    root.addEventListener('click', event => {
      if (event.target.closest('[data-v2-goal-next]')) select(current + 1);
      if (event.target.closest('[data-v2-goal-prev]')) select(current - 1);
    }, { signal });
    root.addEventListener('v2:reveal-block', event => {
      const index = panels.findIndex(panel => panel.dataset.blockId === event.detail.blockId || panel.contains(event.detail.target));
      if (index !== -1) select(index);
    }, { signal });
    select(0);
    tablist.hidden = false;
    root.dataset.v2GoalsMounted = 'true';
  }

  function mountIngredients(root, signal, cleanups) {
    const track = root.querySelector('[data-v2-ingredient-track]');
    const arrows = root.querySelector('[data-v2-ingredient-arrows]');
    if (!track || !arrows) return;
    const previous = root.querySelector('[data-v2-ingredient-prev]');
    const next = root.querySelector('[data-v2-ingredient-next]');
    const update = () => {
      arrows.hidden = track.scrollWidth <= track.clientWidth + 2;
      previous.disabled = track.scrollLeft <= 1;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
    };
    previous.addEventListener('click', () => track.scrollBy({ left: -track.clientWidth * .8, behavior: motion() }), { signal });
    next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth * .8, behavior: motion() }), { signal });
    track.addEventListener('scroll', update, { passive: true, signal });
    let drag;
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('a, button')) return;
      drag = { x: event.clientX, scroll: track.scrollLeft };
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
      event.preventDefault();
    }, { signal });
    track.addEventListener('pointermove', event => {
      if (drag) track.scrollLeft = drag.scroll - (event.clientX - drag.x);
    }, { signal });
    const stop = () => { drag = null; track.classList.remove('is-dragging'); };
    track.addEventListener('pointerup', stop, { signal });
    track.addEventListener('pointercancel', stop, { signal });
    track.querySelectorAll('img').forEach(image => { image.draggable = false; });
    const observer = new ResizeObserver(update);
    observer.observe(track);
    cleanups.push(() => observer.disconnect());
    update();
  }

  function mount(root) {
    if (instances.has(root)) return;
    const controller = new AbortController(), cleanups = [];
    root.querySelectorAll('[data-v2-content-money]').forEach(price => {
      const amount = Number(price.dataset.v2ContentMoney) / 100;
      if (!Number.isFinite(amount) || !price.dataset.currency) return;
      price.textContent = new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency', currency: price.dataset.currency,
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2, maximumFractionDigits: 2
      }).formatToParts(amount).map(part => part.type === 'literal' ? part.value.replace(/\s/g, '') : part.value).join('');
    });
    root.querySelectorAll('[data-v2-goals]').forEach(goals => mountGoals(goals, controller.signal));
    root.querySelectorAll('[data-v2-ingredients]').forEach(ingredients => mountIngredients(ingredients, controller.signal, cleanups));
    instances.set(root, () => { controller.abort(); cleanups.forEach(cleanup => cleanup()); instances.delete(root); });
  }
  function init(scope = document) {
    if (scope.matches?.('[data-v2-editorial-section]')) mount(scope);
    scope.querySelectorAll('[data-v2-editorial-section]').forEach(mount);
  }
  function destroy(scope) {
    if (scope.matches?.('[data-v2-editorial-section]')) instances.get(scope)?.();
    scope.querySelectorAll('[data-v2-editorial-section]').forEach(root => instances.get(root)?.());
  }
  window.MettaEditorial = { init, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    destroy(event.target);
  });
  document.addEventListener('shopify:block:select', event => {
    const scope = event.target.closest?.('[data-v2-editorial-section]') || event.target;
    scope.querySelectorAll('[data-v2-goals]').forEach(goals => goals.dispatchEvent(new CustomEvent('v2:reveal-block', { detail: { blockId: event.detail?.blockId, target: event.target } })));
    const goal = event.target.closest?.('[data-v2-goal]') || event.target.querySelector?.('[data-v2-goal]');
    if (goal) goal.closest('[data-v2-goals]')?.dispatchEvent(new CustomEvent('v2:reveal-block', { detail: { blockId: event.detail?.blockId, target: goal } }));
    const detail = event.target.closest?.('details.v2-faq-item') || event.target.querySelector?.('details.v2-faq-item');
    if (detail) detail.open = true;
  });
})();
